import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";

/**
 * Shared page-header hero for every inner page. Renders Navbar + Footer,
 * an animated gradient hero band, and sets the browser <title>.
 * Children render below the hero band.
 */
export default function PageShell({
  title,
  subtitle,
  eyebrow = "APEX Innovation Solutions",
  breadcrumbs = [],
  seoTitle,
  seoDescription,
  testId,
  children,
}) {
  const location = useLocation();

  useEffect(() => {
    const finalTitle = seoTitle || `${title} | APEX Innovation Solutions`;
    document.title = finalTitle;
    if (seoDescription) {
      let meta = document.querySelector('meta[name="description"]');
      if (meta) meta.setAttribute("content", seoDescription);
    }
    window.scrollTo(0, 0);
  }, [title, seoTitle, seoDescription, location.pathname]);

  return (
    <main data-testid={testId || "page"} className="relative overflow-x-hidden">
      <Navbar />

      <section className="relative pt-40 pb-14 md:pt-48 md:pb-20">
        <div className="absolute inset-0 grid-lines opacity-30 pointer-events-none" />
        <div className="absolute top-[-140px] left-[-160px] w-[520px] h-[520px] rounded-full bg-apex-blue/25 blur-[140px] pointer-events-none" />
        <div className="absolute top-[8%] right-[-160px] w-[480px] h-[480px] rounded-full bg-apex-purple/25 blur-[140px] pointer-events-none" />
        <div className="absolute bottom-[-100px] left-[30%] w-[420px] h-[420px] rounded-full bg-apex-orange/20 blur-[140px] pointer-events-none" />

        <div className="container-apex relative">
          {breadcrumbs.length > 0 && (
            <nav aria-label="Breadcrumb" data-testid="breadcrumb" className="flex items-center gap-1 text-xs text-white/55 mb-6 flex-wrap justify-center md:justify-start">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              {breadcrumbs.map((b, i) => (
                <span key={b.href || b.label} className="flex items-center gap-1">
                  <ChevronRight className="w-3.5 h-3.5 text-white/30" />
                  {b.href ? <Link to={b.href} className="hover:text-white transition-colors">{b.label}</Link> : <span className="text-white/90">{b.label}</span>}
                </span>
              ))}
            </nav>
          )}
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22,1,0.36,1] }} className="max-w-4xl text-center md:text-left mx-auto md:mx-0">
            <div className="eyebrow mb-4 justify-center md:justify-start"><span className="w-6 h-px bg-apex-cyan" /> {eyebrow}</div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-extrabold leading-[1.05] tracking-tight">
              {title}
            </h1>
            {subtitle && (
              <p className="mt-5 text-white/75 text-base md:text-lg leading-relaxed max-w-3xl mx-auto md:mx-0">
                {subtitle}
              </p>
            )}
          </motion.div>
        </div>
      </section>

      {children}

      <Footer />
    </main>
  );
}
